import { useCallback, useEffect, useRef, useState } from "react";
import Confetti from "react-canvas-confetti/dist/presets/fireworks";
import type { TConductorInstance } from "react-canvas-confetti/dist/types";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";
import clsx from "clsx";

import { formatCurrency, getCompletedMonthsForComparison } from "./utils";
import { useMoneySaved } from "./MoneySavedContext";

let confettiFired = false;

function SlotMachineDigit({
  digit,
  delay,
  onComplete,
}: {
  digit: string;
  delay: number;
  onComplete?: () => void;
}) {
  const [displayDigit, setDisplayDigit] = useState("0");
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (digit === "," || digit === "$") {
      setDisplayDigit(digit);
      onComplete?.();
      return;
    }

    const targetNum = parseInt(digit, 10);
    if (isNaN(targetNum)) {
      setDisplayDigit(digit);
      onComplete?.();
      return;
    }

    let currentIteration = 0;
    const totalIterations = 15 + Math.floor(Math.random() * 10);
    const intervalTime = 40;

    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        currentIteration++;
        if (currentIteration < totalIterations) {
          setDisplayDigit(Math.floor(Math.random() * 10).toString());
        } else {
          setDisplayDigit(digit);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          onComplete?.();
        }
      }, intervalTime);
    }, delay);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [digit, delay, onComplete]);

  return (
    <span className="inline-block tabular-nums transition-transform">
      {displayDigit}
    </span>
  );
}

function AnimatedCurrency({
  value,
  onAnimationComplete,
}: {
  value: number;
  onAnimationComplete?: () => void;
}) {
  const formatted = formatCurrency(value);
  const [key, setKey] = useState(0);
  const completedCount = useRef(0);
  const totalDigits = formatted.length;
  const hasCalledComplete = useRef(false);

  useEffect(() => {
    setKey((k) => k + 1);
    completedCount.current = 0;
    hasCalledComplete.current = false;
  }, [value]);

  const handleDigitComplete = useCallback(() => {
    completedCount.current++;
    if (completedCount.current >= totalDigits && !hasCalledComplete.current) {
      hasCalledComplete.current = true;
      onAnimationComplete?.();
    }
  }, [totalDigits, onAnimationComplete]);

  return (
    <span className="inline-flex" key={key}>
      {formatted.split("").map((char, index) => (
        <SlotMachineDigit
          key={`${key}-${index}`}
          digit={char}
          delay={index * 60}
          onComplete={handleDigitComplete}
        />
      ))}
    </span>
  );
}

export const MoneyHero = () => {
  const { totalSavings, monthlySavings } = useMoneySaved();
  const conductorRef = useRef<TConductorInstance | null>(null);
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 200);
    return () => clearTimeout(t1);
  }, []);

  const onConfettiInit = useCallback(
    ({ conductor }: { conductor: TConductorInstance }) => {
      conductorRef.current = conductor;
    },
    [],
  );

  const shootConfetti = useCallback(() => {
    conductorRef.current?.shoot();
  }, []);

  const { current: compareMonth, previous: baseMonth } =
    getCompletedMonthsForComparison(monthlySavings);

  const savingsChange =
    compareMonth && baseMonth && baseMonth.savings !== 0
      ? Math.round(
          ((compareMonth.savings - baseMonth.savings) / baseMonth.savings) *
            100,
        )
      : null;

  const handleCountComplete = useCallback(() => {
    setPhase(2);
    if (totalSavings > 0 && !confettiFired) {
      confettiFired = true;
      shootConfetti();
      setTimeout(() => shootConfetti(), 400);
      setTimeout(() => shootConfetti(), 800);
    }
  }, [totalSavings, shootConfetti]);

  return (
    <div className="flex flex-col items-center justify-center py-8">
      <Confetti onInit={onConfettiInit} globalOptions={{ resize: true }} />
      <span className="animate-gradient-x relative bg-gradient-to-r from-blue-600 via-purple-500 to-blue-600 bg-[length:200%_auto] bg-clip-text text-7xl font-bold tracking-tight text-transparent drop-shadow-sm md:text-8xl">
        {phase >= 1 ? (
          <AnimatedCurrency
            value={totalSavings}
            onAnimationComplete={handleCountComplete}
          />
        ) : (
          <span className="invisible">{formatCurrency(totalSavings)}</span>
        )}
      </span>
      <span
        className={clsx(
          "text-textSubtle mt-3 text-xl font-medium transition-all duration-500",
          phase >= 2 ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0",
        )}
      >
        Total Saved
      </span>
      {savingsChange !== null && (
        <div
          className={clsx(
            "mt-3 flex items-center gap-1 rounded-full px-4 py-1.5 text-lg font-medium transition-all duration-500",
            savingsChange >= 0
              ? "bg-green-100 text-green-600"
              : "bg-gray-100 text-gray-500",
            phase >= 2
              ? "translate-y-0 opacity-100"
              : "translate-y-4 opacity-0",
          )}
        >
          {savingsChange >= 0 ? (
            <ArrowUpRight size={20} />
          ) : (
            <ArrowDownRight size={20} />
          )}
          {Math.abs(savingsChange)}% vs last month
        </div>
      )}
    </div>
  );
};
